/**
 * DENET — hesap araçları
 *
 * Üç hesap: kayıt diski, aydınlatma, kablo kesiti. Hepsi tarayıcıda çalışır,
 * sunucuya istek yok. Formüller ve kabuller ekranda yazılı — sonuç bir kutudan
 * çıkmış gibi görünmemeli, alıcı neye baktığını bilmeli.
 *
 * Değerler tema ayarından değil koddan geliyor: bunlar mühendislik sabitleri,
 * mağaza sahibinin değiştireceği şeyler değil.
 */

import { DenetElement, define, qs, qsa } from '@denet/core';

/* Kanal başına ortalama H.265 bit hızı (Mbps). Üretici tablolarının ortasını
   alır; hareket yoğunluğuna göre gerçekte ±%30 oynar. */
const BITRATE = { 2: 2, 4: 4, 5: 5, 8: 8 };

/* EN 12464-1 ve yaygın TR uygulaması — mekân başına hedef aydınlık (lux). */
const LUX = {
  salon: 150,
  mutfak: 300,
  ofis: 500,
  magaza: 500,
  atolye: 300,
  depo: 150,
  koridor: 100,
  otopark: 75,
};

/* LED armatürde tipik etkinlik (lm/W). Ucuz ürünlerde 80, iyilerinde 130. */
const EFFICACY = 100;

/* Kullanım katsayısı × bakım katsayısı. Işığın ne kadarı çalışma düzlemine
   ulaşıyor — tavan yüksekliği ve yüzey renklerine göre 0.45-0.75 arası. */
const ROOM_FACTOR = 0.6;

/* Bakır özdirenç (Ω·mm²/m). */
const RHO = 0.0175;

/* Standart kesitler ve PVC yalıtımlı bakır iletken için taşıma kapasitesi (A),
   boruda, 30 °C ortam. Gerilim düşümü küçük bir kesit önerse bile akım
   kapasitesi bunu ezer — kablo ısınmadan önce yanar. */
const SECTIONS = [
  { mm2: 1.5, amp: 14 },
  { mm2: 2.5, amp: 18 },
  { mm2: 4, amp: 24 },
  { mm2: 6, amp: 31 },
  { mm2: 10, amp: 42 },
  { mm2: 16, amp: 56 },
  { mm2: 25, amp: 73 },
  { mm2: 35, amp: 89 },
  { mm2: 50, amp: 108 },
  { mm2: 70, amp: 136 },
  { mm2: 95, amp: 164 },
];

const nf = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

class DnTools extends DenetElement {
  mounted() {
    this.panels = qsa('[data-panel]', this);
    this.tabs = qsa('[data-tab]', this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#select(tab.dataset.tab), { signal: this.signal });
    });

    /* Herhangi bir alan değişince o panel yeniden hesaplanır. `input` olayı
       kullanıcı sürüklerken de tetiklenir: sonuç canlı. */
    qsa('input, select', this).forEach((field) => {
      field.addEventListener('input', () => this.#run(field.closest('[data-panel]')), {
        signal: this.signal,
      });
    });

    this.panels.forEach((panel) => this.#run(panel));
    this.#select(this.tabs[0]?.dataset.tab);
  }

  /** @param {string | undefined} name */
  #select(name) {
    if (!name) return;
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== name;
    });
  }

  /** @param {HTMLElement | null} panel */
  #run(panel) {
    if (!panel) return;
    const val = (ref) => {
      const el = qs(`[data-ref="${ref}"]`, panel);
      return el ? Number(el.value) : 0;
    };
    const pick = (ref) => qs(`[data-ref="${ref}"]`, panel)?.value ?? '';
    const out = (ref, text) => {
      const el = qs(`[data-out="${ref}"]`, panel);
      if (el) el.textContent = text;
    };

    /* Sürgülerin yanındaki sayı, sürgüyle birlikte hareket eder. */
    qsa('[data-echo]', panel).forEach((el) => {
      const src = qs(`[data-ref="${el.dataset.echo}"]`, panel);
      if (src) el.textContent = nf0.format(Number(src.value));
    });

    if (panel.dataset.panel === 'disk') {
      const channels = val('channels');
      const days = val('days');
      const mbps = BITRATE[pick('resolution')] ?? 4;

      /* GB/gün/kanal = Mbps × 86400 sn ÷ 8 bit ÷ 1024 MB */
      const gbPerDay = (mbps * 86400) / 8 / 1024;
      const totalTb = (gbPerDay * channels * days) / 1024;

      out('tb', nf.format(totalTb));
      out('perday', nf.format(gbPerDay * channels));
      out('bitrate', nf.format(mbps));

      /* Piyasadaki en yakın üst disk. 24/7 güvenlik diskleri bu boylarda. */
      const disks = [1, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24];
      const suggest = disks.find((d) => d >= totalTb) ?? disks[disks.length - 1];
      out('disk', totalTb > 24 ? `${nf0.format(Math.ceil(totalTb / 12))} × 12 TB` : `${suggest} TB`);
      return;
    }

    if (panel.dataset.panel === 'isik') {
      const area = val('area');
      const lux = LUX[pick('room')] ?? 300;
      const watt = val('watt');

      const lumens = (area * lux) / ROOM_FACTOR;
      const perFixture = watt * EFFICACY;
      const count = perFixture > 0 ? Math.ceil(lumens / perFixture) : 0;

      out('lumens', nf0.format(lumens));
      out('count', nf0.format(count));
      out('lux', nf0.format(lux));
      out('power', nf0.format(count * watt));
      return;
    }

    if (panel.dataset.panel === 'kablo') {
      const amps = val('amps');
      const length = val('length');
      const phase = pick('phase');
      const three = phase === '400';
      const volts = three ? 400 : 230;

      /* İzin verilen gerilim düşümü %3 — aydınlatma için TSE'nin verdiği sınır,
         priz devrelerinde %5'e kadar kabul edilir. Dar olanı seçiyoruz. */
      const dropVolts = volts * 0.03;

      /* Tek faz: gidiş-dönüş iki iletken. Üç faz: √3 katsayısı. */
      const factor = three ? Math.sqrt(3) : 2;
      const byDrop = (factor * length * amps * RHO) / dropVolts;

      const dropSection = SECTIONS.find((s) => s.mm2 >= byDrop) ?? SECTIONS[SECTIONS.length - 1];
      const ampSection = SECTIONS.find((s) => s.amp >= amps) ?? SECTIONS[SECTIONS.length - 1];
      const chosen = dropSection.mm2 >= ampSection.mm2 ? dropSection : ampSection;

      /* Seçilen kesitte gerçek düşüm — kullanıcı sınırın neresinde olduğunu görsün. */
      const realDrop = (factor * length * amps * RHO) / chosen.mm2;

      out('mm2', nf.format(chosen.mm2));
      out('drop', nf.format(realDrop));
      out('droppct', nf.format((realDrop / volts) * 100));
      /* Hangi kısıt kesiti belirledi: uzun hatlarda gerilim düşümü, kısa ve
         yüksek akımlı hatlarda taşıma kapasitesi öne geçer. */
      out('limit', dropSection.mm2 > ampSection.mm2 ? 'gerilim düşümü' : 'akım kapasitesi');
      out('capacity', nf0.format(chosen.amp));
    }
  }
}

define('dn-tools', DnTools);
